
import SessionListElement from '@/model/SessionListElement';
import api, { OrderStatus } from '@/service/api';
import Vue from 'vue'
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { OrderDetails as DetailsModel } from '@/model/OrderDetails';
import { generateWinScript } from "@/service/scriptGen/windows";
import { generateLinuxScript } from "@/service/scriptGen/linux";
import { generateOsxScript } from "@/service/scriptGen/osx";
import OrderDetails from './OrderDetails.vue';

export interface OrderItem {
    orderId: string;
    sessionId: string;
    fullName: string;
    email: string;
    createdAt: {
        date: string;
        timezone_type: number;
        timezone: string;
    };
    price: string;
    chosenPhotos: number;
    chosenPrints: number;
    status: OrderStatus;
}
type WorkflowStatus = 'notStarted' | 'prepared' | 'readyForPickup' | 'done';
type PaymentStatus = 'Niewymagane' | 'Nieopłacone' | 'Opłacone';
type PrintsStatus = 'Nie kupione' | 'Do zamówienia' | 'Wysłane do druku' | 'Do odbioru' | 'Przekazane';
type DigitalStatus = 'Nie kupione' | 'Do sfinalizowania' | 'Wysłane' | 'Pobrane';

@Component({
    components: {
        OrderDetails,
    },
    model: {
        prop: 'selectedOrderIds',
        event: 'update:selectedOrderIds'
    }
})
export default class SingleOrders extends Vue {
    @Prop({
        type: Object,
        required: true,
    })
    session!: SessionListElement;
    @Prop({
        type: Array,
        required: true,
    })
    orders!: Array<OrderItem>;
    @Prop({
        type: Array,
        default: () => []
    })
    selectedOrderIds!: string[];

    details?: { [orderId: string]: DetailsModel } = {};
    workflowStatusByOrderId: { [orderId: string]: WorkflowStatus } = {};
    paymentStatusByOrderId: { [orderId: string]: PaymentStatus } = {};
    printsStatusByOrderId: { [orderId: string]: PrintsStatus } = {};
    digitalStatusByOrderId: { [orderId: string]: DigitalStatus } = {};
    workflowStatuses: { [key in WorkflowStatus]: string } = {
        notStarted: 'Niezaczęte',
        prepared: 'Przygotowane',
        readyForPickup: 'Gotowe do odbioru',
        done: 'Zrobione',
    };
    paymentOptions: PaymentStatus[] = ['Niewymagane', 'Nieopłacone', 'Opłacone'];
    digitalOptions: DigitalStatus[] = ['Nie kupione', 'Do sfinalizowania', 'Wysłane', 'Pobrane'];
    printsOptions: PrintsStatus[] = ['Nie kupione', 'Do zamówienia', 'Wysłane do druku', 'Do odbioru', 'Przekazane'];
    expanded: OrderItem[] = [];
    loadingOrderId: string | null = null;
    sessionSelected = false;

    mounted() {
        this.initializeLocalOrderStates();
        this.updateSessionCheckboxState();
    }

    @Watch('orders', { deep: true })
    onOrdersChanged() {
        this.initializeLocalOrderStates();
        this.updateSessionCheckboxState();
    }

    initializeLocalOrderStates() {
        this.orders.forEach((order) => {
            if (!this.workflowStatusByOrderId[order.orderId]) {
                this.workflowStatusByOrderId[order.orderId] = this.mapBackendStatusToWorkflowStatus(order.status);
            }
            if (!this.paymentStatusByOrderId[order.orderId]) {
                this.paymentStatusByOrderId[order.orderId] = this.mapPaymentStatus(order.status) as PaymentStatus;
            }
            if (!this.printsStatusByOrderId[order.orderId]) {
                this.printsStatusByOrderId[order.orderId] = 'Nie kupione';
            }
            if (!this.digitalStatusByOrderId[order.orderId]) {
                this.digitalStatusByOrderId[order.orderId] = 'Nie kupione';
            }
        });
    }

    mapBackendStatusToWorkflowStatus(status: OrderStatus): WorkflowStatus {
        if (status === 'inprint') return 'prepared';
        if (status === 'sent') return 'readyForPickup';
        if (status === 'paid') return 'done';
        return 'notStarted';
    }

    mapWorkflowStatusToBackendStatus(workflowStatus: WorkflowStatus): OrderStatus {
        if (workflowStatus === 'prepared') return 'inprint';
        if (workflowStatus === 'readyForPickup') return 'sent';
        if (workflowStatus === 'done') return 'paid';
        return 'unpaid';
    }

    mapPaymentStatus(status: OrderStatus): string {
        if (status === 'unpaid') return 'Nieopłacone';
        if (status === 'paid') return 'Opłacone';
        return 'Niewymagane';
    }

    mapPaymentStatusToBackendStatus(paymentStatus: PaymentStatus): OrderStatus {
        if (paymentStatus === 'Opłacone') return 'paid';
        if (paymentStatus === 'Nieopłacone') return 'unpaid';
        return 'cancelled';
    }

    changeStatus({ orderId, workflowStatus }: OrderItem & { workflowStatus: WorkflowStatus }) {
        this.workflowStatusByOrderId[orderId] = workflowStatus;
        return workflowStatus;
    }

    changePaymentStatus(item: OrderItem & { paymentStatus: PaymentStatus; workflowStatus: WorkflowStatus }) {
        // Intentionally decoupled from order workflow status for now.
        // Keep payment selection local in UI without changing backend order status.
        this.paymentStatusByOrderId[item.orderId] = item.paymentStatus;
        return item;
    }

    changePrintsStatus(item: OrderItem & { prints: PrintsStatus }) {
        this.printsStatusByOrderId[item.orderId] = item.prints;
    }

    changeDigitalStatus(item: OrderItem & { digital: DigitalStatus }) {
        this.digitalStatusByOrderId[item.orderId] = item.digital;
    }

    async onItemExpanded({ item, value }: { item: OrderItem, value: boolean }) {
        if (value) {
            // Expanding: show spinner and load details
            await this.loadDetails({ item });
        } else {
            // Collapsing: hide spinner
            this.loadingOrderId = null;
        }
    }

    async loadDetails({ item: { orderId } }: { item: OrderItem }): Promise<DetailsModel> {
        this.loadingOrderId = orderId;
        const details = await api.getOrderDetails(orderId);
        Vue.set(this, 'details', {
            ...this.details,
            [orderId]: details,
        });
        this.loadingOrderId = null;
        return details;
    }

    async copyToClipboard(item: OrderItem) {
        const details = await this.loadDetails({ item });
        navigator.clipboard.writeText(
            details.order.photos
                .map(({ filename }) => /^(.+)\..*$/.exec(filename)![1])
                .reduce((p, f) => p.length === 0 ? f : `${p},${f}`, '')
        );
        (this as any).$vToastify.success({
            title: 'Lista skopiowana',
            body: 'Wklej ją do wyszukiwarki!',
        });
    }

    async downloadScript(item: OrderItem, sessionName: string, os: 'windows'|'osx'|'linux') {
        const details = await this.loadDetails({ item });
        let script;
        let filename;
        switch (os) {
            case 'windows':
                script = generateWinScript(details.order.photos.map(({ filename }) => filename), item.fullName, sessionName);
                filename = 'wybierz-zdjecia.bat';
                break;
            case 'osx':
                script = generateOsxScript(details.order.photos.map(({ filename }) => filename), item.fullName, sessionName);
                filename = 'wybierz-zdjecia.command';
                break;
            case 'linux':
                script = generateLinuxScript(details.order.photos.map(({ filename }) => filename), item.fullName, sessionName);
                filename = 'wybierz-zdjecia.sh';
                break;
            default:
                throw new Error(`Unknown OS ${os}`);
        }
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(script));
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    get items() {
        return this.orders.map(({ createdAt, chosenPrints, chosenPhotos, price, ...rest }, index) => ({
            rowNumber: index + 1,
            createdAt: (new Intl.DateTimeFormat('pl', {
                timeZone: createdAt.timezone
            })).format(new Date(Date.parse(createdAt.date))),
            chosenPrints: `${chosenPhotos} / ${chosenPrints}`,
            price: `${price} zł`,
            agreements: true,
            digital: this.digitalStatusByOrderId[rest.orderId] || 'Nie kupione',
            prints: this.printsStatusByOrderId[rest.orderId] || 'Nie kupione',
            workflowStatus: this.workflowStatusByOrderId[rest.orderId] || this.mapBackendStatusToWorkflowStatus(rest.status),
            paymentStatus: this.paymentStatusByOrderId[rest.orderId] || this.mapPaymentStatus(rest.status),
            ...rest
        }));
    }

    get selectionStatuses() {
        return Object.entries(this.workflowStatuses).map(([ value, text ]) => ({ value, text }));
    }

    get isSessionFullySelected() {
        const sessionOrderIds = this.orders.map(order => order.orderId);
        const selectedSessionOrders = this.selectedOrderIds.filter(id => sessionOrderIds.includes(id));
        return selectedSessionOrders.length === sessionOrderIds.length && sessionOrderIds.length > 0;
    }

    get isSessionPartiallySelected() {
        const sessionOrderIds = this.orders.map(order => order.orderId);
        const selectedSessionOrders = this.selectedOrderIds.filter(id => sessionOrderIds.includes(id));
        return selectedSessionOrders.length > 0 && selectedSessionOrders.length < sessionOrderIds.length;
    }

    get headers() {
        return [
        { text: '', value: 'rowNumber', width: 30, sortable: false },
        { text: '', value: 'select', width: 40 },
            {
                text: 'Klient',
                align: 'start',
                sortable: true,
                value: 'fullName',
                width: '200',
            },
            { text: 'Adres email', value: 'email', width: '200' },
            { text: 'Otrzymano', value: 'createdAt', width: '130' },
            { text: 'Zapłacił', value: 'price', width: '100' },
            { text: 'Zdjęć / Odbitek', value: 'chosenPrints', width: '100' },
            { text: 'Pobierz', value: 'script', width: '100' },
            { text: 'Zgody', value: 'agreements', width: '100' },
            { text: 'Cyfrowe', value: 'digital' },
            { text: 'Odbitki', value: 'prints' },
            { text: 'Płatność', value: 'paymentStatus' },
            { text: 'Status', value: 'status' },
            { text: 'Szczegóły', value: 'data-table-expand', width: '100' },
        ];
    }

    getOrderRowClass(item: OrderItem & { workflowStatus?: WorkflowStatus }) {
        if (item.workflowStatus === 'prepared') return 'status-row-prepared';
        if (item.workflowStatus === 'readyForPickup') return 'status-row-ready';
        if (item.workflowStatus === 'done') return 'status-row-done';
        return 'status-row-not-started';
    }

    getPaymentStatusClass(item: OrderItem & { paymentStatus?: PaymentStatus }) {
        if (item.paymentStatus === 'Opłacone') return 'payment-status-paid';
        if (item.paymentStatus === 'Nieopłacone') return 'payment-status-unpaid';
        return 'payment-status-not-required';
    }

    getPrintsStatusClass(item: OrderItem & { prints?: PrintsStatus }) {
        if (item.prints === 'Do zamówienia') return 'prints-status-to-order';
        if (item.prints === 'Wysłane do druku') return 'prints-status-sent-to-print';
        if (item.prints === 'Do odbioru') return 'prints-status-ready-to-pickup';
        if (item.prints === 'Przekazane') return 'prints-status-handed-over';
        return 'prints-status-not-bought';
    }

    getDigitalStatusClass(item: OrderItem & { digital?: DigitalStatus }) {
        if (item.digital === 'Do sfinalizowania') return 'digital-status-to-finalize';
        if (item.digital === 'Wysłane') return 'digital-status-sent';
        if (item.digital === 'Pobrane') return 'digital-status-downloaded';
        return 'digital-status-not-bought';
    }

    @Watch('selectedOrderIds', { deep: true })
    onSelectedOrderIdsChanged(newVal: string[]) {
        this.$emit('update:selectedOrderIds', newVal);
        // Update session checkbox state based on order selections
        this.sessionSelected = this.isSessionFullySelected;
    }

    onSessionSelectionChange() {
        const sessionOrderIds = this.orders.map(order => order.orderId);
        
        if (this.sessionSelected) {
            // Select all orders in this session
            const newSelectedIds = [...new Set([...this.selectedOrderIds, ...sessionOrderIds])];
            this.$emit('update:selectedOrderIds', newSelectedIds);
        } else {
            // Deselect all orders in this session
            const newSelectedIds = this.selectedOrderIds.filter(id => !sessionOrderIds.includes(id));
            this.$emit('update:selectedOrderIds', newSelectedIds);
        }
    }

    updateSessionCheckboxState() {
        this.sessionSelected = this.isSessionFullySelected;
    }

    isExpanded(item: any) {
        return this.expanded.some(expandedItem => expandedItem.orderId === item.orderId);
    }

    async toggleExpanded(item: any) {
        if (this.isExpanded(item)) {
            this.expanded = this.expanded.filter(i => i.orderId !== item.orderId);
        } else {
            this.expanded = [...this.expanded, item];
            // Load details when expanding
            await this.onItemExpanded({ item, value: true });
        }
    }
}
